const React = require('react');
const T = require('prop-types');
const { useEffect, useRef, useState } = React;
const { context } = require('app-context');

const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: MenuItem } = require('@mui/material/MenuItem');
const { default: TextField } = require('@mui/material/TextField');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: Switch } = require('@mui/material/Switch');

const { EditorState, ContentState } = require('draft-js');
const { Editor } = require('react-draft-wysiwyg');
const { default: htmlToDraft } = require('html-to-draftjs');

require('react-draft-wysiwyg/dist/react-draft-wysiwyg.css');
const { stateToHTML } = require('draft-js-export-html');
const { getDisplayImageUrl } = require('utils/image');

const ImageBlock = require('./ImageBlock');
const { default: Classes } = require('./styles.scss');

const api = context.api.nearpeer;

const internals = {};

internals.escapeAttr = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

internals.stateToHtmlOptions = (contentState) => ({
    blockStyleFn: (block) => {

        const textAlign = block.getData() && block.getData().get('text-align');

        if (textAlign && textAlign !== 'left') {
            return { style: { textAlign } };
        }
    },
    entityStyleFn: (entity) => {

        const entityType = entity.get('type').toLowerCase();

        if (entityType === 'link') {
            const data = entity.getData();

            let safeUrl = data.url.replace('http://', 'https://');
            if (!data.url.startsWith('http://') && !data.url.startsWith('https://')) {
                safeUrl = `https://${safeUrl}`;
            }

            return {
                element: 'a',
                attributes: {
                    href: safeUrl,
                    target: data.targetOption || data.target
                }
            };
        }
    },
    blockRenderers: {
        unstyled: (block) => {

            if (block.getText().replace(/\u00a0/g, ' ').trim() !== '') {
                return;
            }

            return '<p class="welcome-blank-line"><br></p>';
        },
        atomic: (block) => {

            const entityKey = block.getEntityAt(0);

            if (!entityKey) {
                return '';
            }

            const entity = contentState.getEntity(entityKey);

            if (entity.getType() !== 'IMAGE') {
                return;
            }

            const { src, alt } = entity.getData();

            return `<p><img src="${internals.escapeAttr(src)}" alt="${internals.escapeAttr(alt)}" /></p>`;
        }
    }
});

const RichTextEditor = (props) => {

    const { html, onChange, placeholder } = props;

    const [editorState, setEditorState] = useState(() => internals.htmlToEditorState(html));
    const htmlRef = useRef(html || '');

    useEffect(() => {

        if ((html || '') === htmlRef.current) {
            return;
        }

        htmlRef.current = html || '';
        setEditorState(internals.htmlToEditorState(html));
    }, [html]);

    const handleChange = (nextEditorState) => {

        setEditorState(nextEditorState);

        const contentState = nextEditorState.getCurrentContent();
        const nextHtml = stateToHTML(contentState, internals.stateToHtmlOptions(contentState));

        htmlRef.current = nextHtml;
        onChange(nextHtml);
    };

    const uploadCallback = (file) => api.welcomePage.uploadImage({ welcomePageImage: file })
        .then((result) => ({ data: { link: getDisplayImageUrl(result.imageUrl) || result.imageUrl } }));

    const renderImageBlock = (block, config) => {

        if (block.getType() !== 'atomic') {
            return undefined;
        }

        const entityKey = block.getEntityAt(0);

        if (!entityKey) {
            return undefined;
        }

        const entity = config.getEditorState().getCurrentContent().getEntity(entityKey);

        if (entity.getType() !== 'IMAGE') {
            return undefined;
        }

        return {
            component: ImageBlock,
            editable: false,
            props: {
                getEditorState: config.getEditorState,
                onChange: config.onChange
            }
        };
    };

    return (
        <Editor
            editorState={editorState}
            onEditorStateChange={handleChange}
            placeholder={placeholder}
            stripPastedStyles={false}
            wrapperClassName={Classes.editorWrapper}
            editorClassName={Classes.editor}
            customBlockRenderFunc={renderImageBlock}
            toolbar={{
                options: ['inline', 'emoji', 'blockType', 'list', 'textAlign', 'link', 'image'],
                inline: { options: ['bold', 'italic', 'underline'] },
                blockType: { inDropdown: false, options: ['Normal', 'H1', 'H2', 'H3'] },
                list: { options: ['unordered', 'ordered'], inDropdown: true },
                textAlign: { inDropdown: false, options: ['left', 'center', 'right', 'justify'] },
                link: { defaultTargetOption: '_blank' },
                image: {
                    uploadCallback,
                    previewImage: true,
                    alt: { present: true, mandatory: false },
                    inputAccept: 'image/jpeg,image/png,image/gif,image/webp',
                    defaultSize: {
                        width: 'auto',
                        height: 'auto'
                    }
                }
            }}
        />
    );
};

RichTextEditor.propTypes = {
    html: T.string,
    onChange: T.func.isRequired,
    placeholder: T.string
};

module.exports = function WelcomePageManagement(props) {

    const {
        status,
        settings,
        fetchSettings,
        updateSchoolText,
        updateRoleText
    } = props;

    const [schoolHtml, setSchoolHtml] = useState('');
    const [schoolHtmlDirty, setSchoolHtmlDirty] = useState(false);
    const [savingSchool, setSavingSchool] = useState(false);

    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [roleHtml, setRoleHtml] = useState('');
    const [roleHtmlDirty, setRoleHtmlDirty] = useState(false);
    const [roleHomeTextEnabled, setRoleHomeTextEnabled] = useState(true);
    const [savingRole, setSavingRole] = useState(false);
    const [savingRoleVisibility, setSavingRoleVisibility] = useState(false);

    const roles = (settings && settings.roles) || [];

    useEffect(() => {

        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {

        if (settings && !schoolHtmlDirty) {
            setSchoolHtml(settings.homeText || '');
        }
    }, [settings, schoolHtmlDirty]);

    useEffect(() => {

        if (settings) {
            setRoleHomeTextEnabled(settings.roleHomeTextEnabled ?? true);
        }
    }, [settings]);

    useEffect(() => {

        if (roles.length && !selectedRoleId) {
            setSelectedRoleId(roles[0].id);
        }
    }, [roles, selectedRoleId]);

    useEffect(() => {

        if (roleHtmlDirty) {
            return;
        }

        const role = roles.find((item) => item.id === selectedRoleId);

        setRoleHtml(role ? (role.homeText || '') : '');
    }, [roles, selectedRoleId, roleHtmlDirty]);

    const handleSaveSchool = async () => {

        setSavingSchool(true);

        try {
            await updateSchoolText({ homeText: schoolHtml });
            setSchoolHtmlDirty(false);
        }
        finally {
            setSavingSchool(false);
        }
    };

    const handleCancelSchool = () => {

        setSchoolHtml((settings && settings.homeText) || '');
        setSchoolHtmlDirty(false);
    };

    const handleSaveRole = async () => {

        if (!selectedRoleId) {
            return;
        }

        setSavingRole(true);

        try {
            await updateRoleText({ roleId: selectedRoleId, homeText: roleHtml });
            setRoleHtmlDirty(false);
        }
        finally {
            setSavingRole(false);
        }
    };

    const handleCancelRole = () => {

        const role = roles.find((item) => item.id === selectedRoleId);

        setRoleHtml(role ? (role.homeText || '') : '');
        setRoleHtmlDirty(false);
    };

    const handleToggleRoleMessage = async (event) => {

        const enabled = event.target.checked;

        setRoleHomeTextEnabled(enabled);
        setSavingRoleVisibility(true);

        try {
            const result = await updateSchoolText({ roleHomeTextEnabled: enabled });

            if (!result) {
                setRoleHomeTextEnabled(!enabled);
            }
        }
        finally {
            setSavingRoleVisibility(false);
        }
    };

    if (status === 'loading' && !settings) {
        return (
            <div className={Classes.loadingWrapper}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className={Classes.wrapper}>
            <h2>Homepage</h2>

            <div className={Classes.section}>
                <Typography variant='h6'>School-wide Message</Typography>
                <Typography variant='body2' color='text.secondary' className={Classes.sectionDescription}>
                    Shown to everyone on the welcome screen, above any role-specific message.
                </Typography>
                <RichTextEditor
                    html={schoolHtml}
                    placeholder='Write a welcome message for your whole community...'
                    onChange={(html) => {

                        setSchoolHtml(html);
                        setSchoolHtmlDirty(true);
                    }}
                />
                <div className={Classes.buttonRow}>
                    <Button
                        variant='outlined'
                        onClick={handleCancelSchool}
                        disabled={savingSchool || !schoolHtmlDirty}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSaveSchool}
                        disabled={savingSchool}
                    >
                        {savingSchool ? <CircularProgress size={20} /> : 'Save School Message'}
                    </Button>
                </div>
            </div>

            <div className={Classes.section}>
                <div className={Classes.sectionHeader}>
                    <Typography variant='h6'>Role-specific Message</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={roleHomeTextEnabled}
                                color='primary'
                                disabled={savingRoleVisibility}
                                onChange={handleToggleRoleMessage}
                            />
                        }
                        label='Show on homepage'
                        labelPlacement='start'
                    />
                </div>
                <Typography variant='body2' color='text.secondary' className={Classes.sectionDescription}>
                    Shown only to users signed up with the selected role.
                </Typography>
                {roleHomeTextEnabled && (
                    <React.Fragment>
                        <TextField
                            select
                            label='Role'
                            value={selectedRoleId}
                            onChange={(event) => {

                                setSelectedRoleId(event.target.value);
                                setRoleHtmlDirty(false);
                            }}
                            fullWidth
                            className={Classes.roleSelect}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>{role.label}</MenuItem>
                            ))}
                        </TextField>
                        <RichTextEditor
                            html={roleHtml}
                            placeholder='Write a welcome message for this role...'
                            onChange={(html) => {

                                setRoleHtml(html);
                                setRoleHtmlDirty(true);
                            }}
                        />
                        <div className={Classes.buttonRow}>
                            <Button
                                variant='outlined'
                                onClick={handleCancelRole}
                                disabled={savingRole || !roleHtmlDirty}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                onClick={handleSaveRole}
                                disabled={savingRole || !selectedRoleId}
                            >
                                {savingRole ? <CircularProgress size={20} /> : 'Save Role Message'}
                            </Button>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

module.exports.propTypes = {
    status: T.string,
    settings: T.shape({
        homeText: T.string,
        roleHomeTextEnabled: T.bool,
        roles: T.arrayOf(T.shape({
            id: T.number,
            name: T.string,
            label: T.string,
            homeText: T.string
        }))
    }),
    fetchSettings: T.func.isRequired,
    updateSchoolText: T.func.isRequired,
    updateRoleText: T.func.isRequired
};

internals.countBlankParagraphsBeforeImages = (html) => {

    const counts = [];
    const imgRegex = /<img\b[^>]*>/gi;
    const blankSuffix = /(?:<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\u00a0|\s)*<\/p>\s*)+$/i;
    let match;

    while ((match = imgRegex.exec(html))) {
        const before = html.slice(0, match.index);
        const blanks = before.match(blankSuffix);

        counts.push(blanks ? (blanks[0].match(/<p/gi) || []).length : 0);
    }

    return counts;
};

internals.htmlToEditorState = (html) => {

    if (!html) {
        return EditorState.createEmpty();
    }

    const normalizedHtml = html
        .replace(/<\/?figure[^>]*>/gi, '')
        .replace(/<p[^>]*class="welcome-blank-line"[^>]*>[\s\S]*?<\/p>/gi, '<p><br></p>')
        .replace(/<p[^>]*>\s*(<img\b[^>]*>)\s*<\/p>/gi, '$1');

    const { contentBlocks, entityMap } = htmlToDraft(normalizedHtml);
    const contentState = ImageBlock.trimEmptyBlocksBeforeImages(
        ImageBlock.liftImagesToAtomicBlocks(
            ContentState.createFromBlockArray(contentBlocks, entityMap)
        ),
        internals.countBlankParagraphsBeforeImages(normalizedHtml)
    );

    return EditorState.createWithContent(contentState);
};
